import Image, { getImageProps } from "next/image";
import { ArrowRightIcon } from "@phosphor-icons/react/ssr";

import GTMEventLink from "~/components/ui/GTMEventLink";
import Heading from "~/componentsDS/Typography/Heading";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { Product } from "./data";

const DESKTOP_IMAGE_SIZES = "(max-width: 1023px) 300px, (max-width: 1599px) 500px, 500px";
const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

type ProductCardProps = {
    product: Product;
};

export const ProductCard = ({ product }: ProductCardProps) => {
    const {
        props: {
            srcSet: hoverSrcSet,
            ...hoverImageProps
        },
    } = getImageProps({
        src: product.imageHoverUrl,
        alt: "",
        fill: true,
        sizes: DESKTOP_IMAGE_SIZES,
        quality: 95,
        loading: "lazy",
    });

    return (
        <GTMEventLink
            href={product.href}
            className="group relative block h-[288px] md:h-[352px] w-full overflow-hidden rounded-[16px] bg-[#f5f5f7] text-primary500 md:h-[360px]"
            aria-label={`${product.title}: ${product.name}`}
            gtmEventData={{
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                    widget: "our_products",
                    meta_data: product.analyticEvent,
                },
            }}
        >
            <Image
                src={product.imageUrl}
                alt={product.imageAlt}
                fill
                sizes="(max-width: 767px) 240px, (max-width: 1023px) 300px, (max-width: 1599px) 500px, 500px"
                quality={95}
                loading="lazy"
                className="object-cover object-center transition-[opacity,transform] duration-500 ease-out md:group-hover:scale-105 md:group-hover:opacity-0"
            />
            <picture>
                <source
                    media="(min-width: 768px)"
                    srcSet={hoverSrcSet}
                    sizes={DESKTOP_IMAGE_SIZES}
                />
                <img
                    {...hoverImageProps}
                    src={TRANSPARENT_PIXEL}
                    alt=""
                    className="scale-[1.02] object-cover object-center opacity-0 transition-[opacity,transform] duration-500 ease-out md:group-hover:scale-100 md:group-hover:opacity-100"
                />
            </picture>

            <div className="relative flex h-full flex-col justify-between px-4 py-3 md:p-5">
                <Heading
                    as="h3"
                    size="2xs"
                    weight="500"
                    className="flex items-start max-md:justify-between gap-1.5 leading-[1.15] group-hover:underline"
                >
                    <span>{product.title}</span>
                    <ArrowRightIcon aria-hidden="true" className="mt-[-1px] shrink-0" size={20} />
                </Heading>

                <Heading
                    as="p"
                    font="featureDisplay"
                    weight="400"
                    className="text-[28px]"
                >
                    <TextWithRegisteredMark text={product.name} />
                </Heading>
            </div>
        </GTMEventLink>
    );
}
